import {
	Box,
	Button,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useApiMutation } from "src/hooks/useApiMutation";
import { useGetQueryWithTotal } from "src/hooks/useGetQueryWithTotal";
import type {
	IGenerateWinnerPayload,
	IRafflePrize,
} from "src/Types/module/raffleDraw/raffleDraw";

interface Props {
	handleClose: () => void;
}

const GenerateWinnerModal: React.FC<Props> = ({ handleClose }) => {
	const [selectedPrizeId, setSelectedPrizeId] = useState<number | "">("");

	const { data: prizeData, isLoading: isPrizesLoading } = useGetQueryWithTotal<
		IRafflePrize[]
	>({
		endPoint: "/api/raffle-draw/prize",
		queryKeys: ["raffle-prize-for-winner"],
		params: { limit: 100, skip: 0 },
	});

	const { mutate: drawWinner, isPending, isSuccess } = useApiMutation<
		IGenerateWinnerPayload,
		void
	>({
		method: "POST",
		endPoint: "/api/raffle-draw/winner",
		invalidateQueries: [
			"raffle-winner",
			"raffle-prize",
			"raffle-participant",
		],
	});

	useEffect(() => {
		if (isSuccess) {
			handleClose();
		}
	}, [isSuccess]);

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (!selectedPrizeId) {
			alert("Please select a prize to draw a winner.");
			return;
		}

		drawWinner({
			prize_id: Number(selectedPrizeId),
		});
	};

	const prizes = prizeData?.data || [];

	return (
		<Box
			component="form"
			onSubmit={handleSubmit}
			sx={{
				display: "flex",
				flexDirection: "column",
				gap: 2.5,
				pt: 1,
				pb: 0.5,
			}}
		>
			<Typography variant="body2" color="text.secondary">
				Select a prize from the list below to automatically pick a random lucky winner among registered participants.
			</Typography>

			<FormControl fullWidth size="small" required>
				<InputLabel id="select-prize-label">Select Prize</InputLabel>
				<Select
					labelId="select-prize-label"
					label="Select Prize"
					value={selectedPrizeId}
					onChange={(e) => setSelectedPrizeId(e.target.value as number | "")}
					disabled={isPrizesLoading}
				>
					<MenuItem value="">
						<em>-- Select a Prize --</em>
					</MenuItem>
					{prizes.map((prize) => (
						<MenuItem
							key={prize.id}
							value={prize.id}
							disabled={prize.is_won}
						>
							<Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", alignItems: "center" }}>
								<span>
									<b>{prize.prize_no}</b> - {prize.prize_details || "No Details"}
								</span>
								{prize.is_won && (
									<Typography
										variant="caption"
										sx={{
											color: "warning.main",
											fontWeight: 600,
											ml: 1,
										}}
									>
										(Already Won)
									</Typography>
								)}
							</Box>
						</MenuItem>
					))}
				</Select>
			</FormControl>

			<Box
				sx={{
					display: "flex",
					justifyContent: "flex-end",
					gap: 1.5,
					mt: 1,
				}}
			>
				<Button
					variant="outlined"
					color="inherit"
					onClick={handleClose}
					disabled={isPending}
				>
					<span>Cancel</span>
				</Button>
				<Button
					type="submit"
					variant="contained"
					color="primary"
					disabled={isPending || !selectedPrizeId}
					sx={{ minWidth: 140, fontWeight: 600, textTransform: "none" }}
				>
					<span>{isPending ? "Drawing..." : "Draw Winner"}</span>
				</Button>
			</Box>
		</Box>
	);
};

export default GenerateWinnerModal;
